import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'

import style from './GausiumMapPreviewOverlay.module.css'

export interface GausiumMapPreviewOverlayProps {
  /** When false, nothing is rendered. */
  visible: boolean
  previewUrl: string | null | undefined
  mapName?: string
  /** Ref to the map widget root (e.g. mapWrapper). Clicks outside this node close the overlay. */
  mapWidgetRootRef: RefObject<HTMLElement | null>
  onClose: () => void
}

function resolveMediaSrc(url: string): string {
  const t = url.trim()
  if (t.startsWith('http://') || t.startsWith('https://')) return t
  if (typeof window !== 'undefined' && t.startsWith('/')) {
    return `${window.location.origin}${t}`
  }
  return t
}

/**
 * Full-bleed floor-plan preview over the map panel. Image is centered and limited by the widget (object-fit: contain).
 * Close: dim layer (around image), click outside map widget, Esc, ×.
 */
const GausiumMapPreviewOverlay = ({
  visible,
  previewUrl,
  mapName,
  mapWidgetRootRef,
  onClose,
}: GausiumMapPreviewOverlayProps) => {
  const { t } = useTranslation()
  const innerRef = useRef<HTMLDivElement>(null)

  const handleDimClick = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [visible, onClose])

  useEffect(() => {
    if (!visible) return
    const onMouseDown = (e: MouseEvent) => {
      const root = mapWidgetRootRef.current
      if (!root || root.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('mousedown', onMouseDown, true)
    return () => document.removeEventListener('mousedown', onMouseDown, true)
  }, [visible, onClose, mapWidgetRootRef])

  if (!visible) return null

  const hasImage = !!previewUrl?.trim()
  const src = hasImage ? resolveMediaSrc(previewUrl!.trim()) : ''

  return (
    <div className={style.layer} data-test-id="gausium-map-preview-overlay">
      <button type="button" className={style.dim} onClick={handleDimClick} aria-label="Close map preview" />
      <div
        ref={innerRef}
        className={style.content}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mapName?.trim() || 'Gausium map preview'}
      >
        <div className={style.header}>
          <div className={style.title}>{mapName?.trim() || 'Gausium map'}</div>
          <button type="button" className={style.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {hasImage ? (
          <div className={style.imgWrap}>
            <img
              className={style.img}
              src={src}
              alt=""
              draggable={false}
              onError={() => {
                console.warn('[GausiumMapPreviewOverlay] failed to load image', src)
              }}
            />
          </div>
        ) : (
          <div className={style.noPreview}>
            <div className={style.noPreviewInner}>{t('gausium_map.preview_unavailable')}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GausiumMapPreviewOverlay
